/**
 * @fileoverview list of supported countries along with their language config and list of supported features.
 *
 * Note that languages will be returned in their full locale formatted string even if they are not defined as such
 * in this config file.  For example, if AU's default language is `en`  when the getter to get the default language is invoked
 * the language will be `en_AU`.
 *
 * SupportedLanguages is a list of that countries official supported languages. If a particular
 * dialect is required you may specify it here.  This is only useful when used in conjuction with vue's i18n fallback locale
 * logic and it will not show up as part of the language config getter.
 */
import type { RawRegionConfig } from './models/types/regionConfig.js'

const precheckRememberMe = 'PRECHECK_REMEMBER_ME'
const disableFeedback = 'DISABLE_FEEDBACK'
const enableAddressAutocomplete = 'ENABLE_ADDRESS_AUTOCOMPLETE'
const enableSupportPage = 'ENABLE_SUPPORT_PAGE'
const explicitTermsConsent = 'EXPLICIT_TERMS_CONSENT'
const explicitPrivacyConsent = 'EXPLICIT_PRIVACY_CONSENT'
const explicitFraudProtectionConsent = 'EXPLICIT_FRAUD_PROTECTION_CONSENT'

/**
 * Mapping of languages to default locale to use for that language.
 */
export const languageDefaultLocales = {
  bg: 'bg-BG',
  cs: 'cs-CZ',
  de: 'de-DE',
  el: 'el-GR',
  en: 'en-US',
  // es-LA used to represent Latin American Spanish
  es: 'es-LA',
  et: 'et-EE',
  fi: 'fi-FI',
  fr: 'fr-FR',
  hr: 'hr-HR',
  hu: 'hu-HU',
  id: 'id-ID',
  it: 'it-IT',
  lt: 'lt-LT',
  lv: 'lv-LV',
  ms: 'ms-MY',
  nb: 'nb-NO',
  nl: 'nl-NL',
  pl: 'pl-PL',
  pt: 'pt-BR',
  ro: 'ro-RO',
  ru: 'ru-BY',
  sk: 'sk-SK',
  sr: 'sr-RS',
  sv: 'sv-SE',
  th: 'th-TH',
  uk: 'uk-UA',
  vn: 'vi-VN',
  zh: 'zh-HK'
}

export const configRules: Record<string, Omit<RawRegionConfig, 'rules'>> = {
  // UAE
  AE: {
    showInLocaleSelector: true
  },

  // Argentina
  AR: {
    addressFieldsSortOrder: ['name', 'country', ['streetName', 'streetNumber'], 'lineFields', 'city', 'state', 'zip'],
    showInLocaleSelector: true
  },

  // Austria
  AT: {
    features: [explicitTermsConsent, enableAddressAutocomplete],
    addressFieldsSortOrder: ['name', 'lineFields', 'zip', 'city', 'country'],
    showInLocaleSelector: true
  },

  // Australia
  AU: {
    features: [enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Belgium
  BE: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city', 'state'],
    features: [explicitTermsConsent, explicitPrivacyConsent, enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Bulgaria
  BG: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city'],
    features: [disableFeedback, enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Bahrain
  BH: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city', 'state'],
    showInLocaleSelector: true
  },

  // Brazil
  BR: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', ['complemento', 'neighborhood'], 'city', ['state', 'zip']],
    showInLocaleSelector: true
  },

  // Canada
  CA: {
    features: [explicitTermsConsent, explicitPrivacyConsent],
    showInLocaleSelector: true
  },

  // Switzerland
  CH: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city'],
    features: [enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Chile
  CL: {
    addressFieldsSortOrder: ['name', 'country', ['streetName', 'streetNumber'], 'lineFields', 'city', 'state', 'zip'],
    showInLocaleSelector: true
  },

  // Colombia
  CO: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'state', 'city', 'zip'],
    showInLocaleSelector: true
  },

  // Costa Rica
  CR: {
    features: [enableAddressAutocomplete],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city', 'state', 'zip'],
    showInLocaleSelector: true
  },

  // Czech Republic
  CZ: {
    features: [enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Germany
  DE: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city'],
    features: [explicitTermsConsent, explicitPrivacyConsent, enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Denmark
  DK: {
    features: [explicitTermsConsent, enableAddressAutocomplete],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city'],
    showInLocaleSelector: true
  },

  // Dominican Republic
  DO: {
    features: [disableFeedback],
    addressFieldsSortOrder: ['name', 'country', ['streetName', 'streetNumber'], 'lineFields', 'state', ['city', 'zip']],
    showInLocaleSelector: true
  },

  // Ecuador
  EC: {
    features: [disableFeedback],
    addressFieldsSortOrder: ['name', 'country', ['streetName', 'streetNumber'], 'lineFields', 'state', ['city', 'zip']],
    showInLocaleSelector: true
  },

  // Estonia
  EE: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city'],
    features: [enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Egypt
  EG: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city', 'zip'],
    features: [disableFeedback, enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Spain
  ES: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city', 'state'],
    features: [enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Finland
  FI: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city', 'state'],
    features: [enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // France
  FR: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city'],
    features: [explicitTermsConsent],
    showInLocaleSelector: true
  },

  // United Kingdom
  GB: {
    features: [enableAddressAutocomplete],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city', 'state'],
    showInLocaleSelector: true
  },

  // Georgia
  GE: {
    beta: true,
    features: [explicitTermsConsent, explicitPrivacyConsent, enableAddressAutocomplete],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city', 'zip'],
    showInLocaleSelector: true
  },

  // Ghana
  GH: {
    beta: true,
    features: [explicitTermsConsent, explicitPrivacyConsent, enableAddressAutocomplete],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city', 'zip'],
    showInLocaleSelector: true
  },

  // Greece
  GR: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city'],
    features: [disableFeedback, enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Guatemala
  GT: {
    features: [disableFeedback],
    addressFieldsSortOrder: ['name', 'country', ['streetName', 'streetNumber'], 'lineFields', 'state', ['city', 'zip']],
    showInLocaleSelector: true
  },

  // Hong Kong
  HK: {
    features: [explicitTermsConsent, explicitPrivacyConsent, enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Honduras
  HN: {
    features: [disableFeedback],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'state', ['city', 'zip']],
    showInLocaleSelector: true
  },

  // Croatia
  HR: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city'],
    features: [disableFeedback, enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Hungary
  HU: {
    addressFieldsSortOrder: ['name', 'city', 'lineFields', 'zip', 'country'],
    features: [disableFeedback, enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Ireland
  IE: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city', 'state', 'zip'],
    showInLocaleSelector: true
  },

  // Israel
  IL: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city'],
    features: [disableFeedback, enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // India
  IN: {
    beta: true,
    betaLanguages: ['hi-IN'],
    features: [explicitTermsConsent, explicitPrivacyConsent, enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Italy
  IT: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city', 'zip', 'state'],
    features: [enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Jordan
  JO: {
    features: [enableAddressAutocomplete],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city', 'zip'],
    showInLocaleSelector: true
  },

  // Kenya
  KE: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city', 'zip'],
    features: [enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Kuwait
  KW: {
    showInLocaleSelector: true
  },

  // Lithuania
  LT: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city'],
    features: [enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Latvia
  LV: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city'],
    features: [enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Marshall Islands
  MH: {
    beta: true,
    showInLocaleSelector: true
  },

  // Malaysia
  MY: {
    features: [explicitTermsConsent, explicitPrivacyConsent],
    showInLocaleSelector: true
  },

  // Mauritius
  MU: {
    beta: true,
    features: [explicitTermsConsent, explicitPrivacyConsent, enableAddressAutocomplete],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city', 'zip'],
    showInLocaleSelector: true
  },

  // Mexico
  MX: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city', 'state', 'zip'],
    showInLocaleSelector: true
  },

  // Nigeria
  NG: {
    features: [enableAddressAutocomplete],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city', 'zip'],
    showInLocaleSelector: true
  },

  // Nicaragua
  NI: {
    beta: true,
    features: [disableFeedback],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'state', ['city', 'zip']],
    showInLocaleSelector: true
  },

  // Netherlands
  NL: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city'],
    features: [enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Norway
  NO: {
    features: [enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // New Zealand
  NZ: {
    features: [enableAddressAutocomplete, explicitTermsConsent, explicitPrivacyConsent],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city', 'zip'],
    showInLocaleSelector: true
  },

  // Oman
  OM: {
    beta: true,
    features: [explicitTermsConsent, explicitPrivacyConsent, enableAddressAutocomplete],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city', 'zip'],
    showInLocaleSelector: true
  },

  // Panama
  PA: {
    features: [disableFeedback],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'state', ['city', 'zip']],
    showInLocaleSelector: true
  },

  // Peru
  PE: {
    addressFieldsSortOrder: ['name', 'country', ['streetName', 'streetNumber'], 'lineFields', 'city', 'state', 'zip'],
    showInLocaleSelector: true
  },

  // Philippines
  PH: {
    features: [explicitPrivacyConsent, enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Pakistan
  PK: {
    beta: true,
    features: [explicitTermsConsent, explicitPrivacyConsent, enableAddressAutocomplete],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city', 'state', 'zip'],
    showInLocaleSelector: true
  },

  // Poland
  PL: {
    features: [enableAddressAutocomplete],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city'],
    showInLocaleSelector: true
  },

  // Portugal
  PT: {
    features: [enableAddressAutocomplete],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city'],
    showInLocaleSelector: true
  },

  // Paraguay
  PY: {
    features: [disableFeedback],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'state', ['city', 'zip']],
    showInLocaleSelector: true
  },

  // Qatar
  QA: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city', 'state', 'zip'],
    showInLocaleSelector: true
  },

  // Romania
  RO: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city'],
    features: [disableFeedback, enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Serbia
  RS: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city'],
    features: [disableFeedback, enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Rwanda
  RW: {
    beta: true,
    features: [explicitTermsConsent, explicitPrivacyConsent, enableAddressAutocomplete],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city', 'zip'],
    showInLocaleSelector: true
  },

  // Saudi Arabia
  SA: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city', 'state', 'zip'],
    showInLocaleSelector: true
  },

  // Sweden
  SE: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city', 'state'],
    features: [enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Singapore
  SG: {
    features: [explicitTermsConsent, explicitPrivacyConsent, enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // Slovakia
  SK: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city', 'state'],
    features: [enableAddressAutocomplete],
    showInLocaleSelector: true
  },

  // El Salvador
  SV: {
    beta: true,
    features: [disableFeedback],
    addressFieldsSortOrder: ['name', 'country', ['streetName', 'streetNumber'], 'lineFields', 'state', ['city', 'zip']],
    showInLocaleSelector: true
  },

  // Thailand
  TH: {
    beta: true,
    features: [explicitTermsConsent, explicitPrivacyConsent, enableAddressAutocomplete],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city', 'state', 'zip'],
    showInLocaleSelector: true
  },

  // Tanzania
  TZ: {
    features: [enableAddressAutocomplete],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city', 'zip'],
    showInLocaleSelector: true
  },

  // Ukraine
  UA: {
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'zip', 'city', 'state'],
    showInLocaleSelector: true
  },

  // Uganda
  UG: {
    beta: true,
    features: [explicitTermsConsent, explicitPrivacyConsent, enableAddressAutocomplete],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city', 'zip'],
    showInLocaleSelector: true
  },

  // United States
  US: {
    features: [precheckRememberMe, enableAddressAutocomplete, enableSupportPage],
    promotionalMerchants: [
      { name: 'bass-pro', url: 'https://www.basspro.com/shop/en' },
      { name: 'saks', url: 'https://www.saksfifthavenue.com/Entry.jsp' },
      { name: 'staples', url: 'https://www.staples.com/' },
      { name: 'expedia', url: 'https://www.expedia.com/?pwaLob=wizard-hotel-pwa-v2' },
      { name: 'barnes-and-noble', url: 'https://www.barnesandnoble.com/' },
      { name: 'priceless-planet', url: 'https://www.mastercard.us/en-us/vision/corp-responsibility/priceless-planet.html' }
    ],
    showInLocaleSelector: true
  },

  // Uruguay
  UY: {
    beta: true,
    features: [disableFeedback],
    addressFieldsSortOrder: ['name', 'country', ['streetName', 'streetNumber'], 'lineFields', ['city', 'zip']],
    showInLocaleSelector: true
  },

  // Vietnam
  VN: {
    beta: true,
    features: [precheckRememberMe, enableAddressAutocomplete, explicitFraudProtectionConsent],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city'],
    showInLocaleSelector: true
  },

  // South Africa
  ZA: {
    showInLocaleSelector: true
  },

  // Zambia
  ZM: {
    beta: true,
    features: [explicitTermsConsent, explicitPrivacyConsent, enableAddressAutocomplete],
    addressFieldsSortOrder: ['name', 'country', 'lineFields', 'city'],
    showInLocaleSelector: true
  }
}
